import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import type { MSBRegistration, OnboardingDocumentRequirementResult } from '@gcv/shared';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Box, useMediaQuery, useTheme } from '@mui/material';

import { MsbsApi } from 'api';
import { getSnackbarStore } from 'stores/SnackBarStore';
import { useMsbRegistrationsSearch } from 'tanstack-hooks';
import type { Column, Row } from 'ui';
import {
  Button,
  Dialog,
  Error,
  InputLabel,
  InputText,
  Spinner,
  Table,
  TableContainer,
  Text,
  ZeroState
} from 'ui';
import { DateTimeHelpers } from 'util/dateTime.util';
import { getMimeType, openFileInNewWindow } from 'util/files.util';
import { viewFile } from 'util/s3.util';

interface MsbRegistrationSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (selectedRegs: MSBRegistration[]) => void;
  onCancel: () => void;
  showUploadForm: () => void;
  dispensaryId: string;
  requirementResult?: OnboardingDocumentRequirementResult;
}

interface MsbRegistrationTableData {
  id: string;
  name: string;
  dba?: string;
  address: string;
  signedDate: string;
  number: string;
  pdf: string;
  rawRecord: MSBRegistration;
}

export const MsbRegistrationSearchModal: React.FC<MsbRegistrationSearchModalProps> = (props) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

  const msbsApi = new MsbsApi();

  const form = useForm({ mode: 'onBlur' });
  const { isOpen, onClose, onSubmit, onCancel, showUploadForm, dispensaryId, requirementResult } = props;

  const { msbRegistrationSearchResults, isLoadingMsbRegistrationSearchResults } =
    useMsbRegistrationsSearch(dispensaryId);

  const [searchingRegistrations, setSearchingRegistrations] = useState<boolean>(false);
  const [registrationRows, setRegistrationRows] = useState<Row<MsbRegistrationTableData>[]>([]);
  const [selectedRegistrations, setSelectedRegistrations] = useState<MSBRegistration[]>([]);
  const [searchError, setSearchError] = useState<string>('');

  const alreadyUploadedRegNumbers = Object.values(requirementResult?.documents ?? {})
    .map((d) => d.lifecycle_values?.identifier?.value)
    .filter(Boolean);

  const mapRegistrationToRow = (reg: MSBRegistration, highlight?: boolean): Row<MsbRegistrationTableData> => {
    return {
      id: reg.id,
      highlightBackground: highlight ? '#e8f5e9' : undefined,
      disabled: alreadyUploadedRegNumbers.includes(reg.registration_number),
      disableSelect: alreadyUploadedRegNumbers.includes(reg.registration_number),
      disabledTooltip: "You've already provided this registration",
      data: {
        id: reg.id,
        name: reg.legal_name,
        dba: reg.dba_name,
        address: [reg.street_address, reg.city, reg.state, reg.zip].filter(Boolean).join(' '),
        signedDate: reg.auth_sign_date,
        pdf: reg.s3_key!,
        number: reg.registration_number ?? '--',
        rawRecord: reg
      }
    };
  };

  useEffect(() => {
    if (msbRegistrationSearchResults && isOpen) {
      const msbRows: Row<MsbRegistrationTableData>[] = msbRegistrationSearchResults
        .filter((m) => m.s3_key)
        .map((reg) => mapRegistrationToRow(reg, false));
      setRegistrationRows(msbRows);
    } else {
      setRegistrationRows([]);
    }
  }, [msbRegistrationSearchResults, isOpen]);

  const msbColumns: Column<MsbRegistrationTableData>[] = [
    {
      id: 'name',
      label: 'Legal Name'
    },
    {
      id: 'dba',
      label: 'DBA',
      format: (row) => (!row.dba ? '--' : row.dba)
    },
    {
      id: 'address',
      label: 'Address',
      widthPercent: 25
    },
    {
      id: 'number',
      label: 'Reg. Number',
      widthPercent: 17
    },
    {
      id: 'signedDate',
      label: 'Signed Date',
      format: (row) =>
        row.signedDate ? DateTimeHelpers.formatDateStringToTableDateString(row.signedDate, undefined) : '--'
    },
    {
      id: 'pdf',
      label: 'PDF',
      widthPercent: 10,
      format: (row) =>
        row.pdf != null ? (
          <OpenInNewIcon onClick={() => openDocument(row.pdf!)} sx={{ cursor: 'pointer' }} />
        ) : (
          ''
        )
    }
  ];

  const openDocument = async (s3Link: string) => {
    const blob = await viewFile(s3Link);
    if (blob) {
      openFileInNewWindow(blob, getMimeType(s3Link), s3Link.split('/').reverse()[0]);
    }
  };

  const searchForRegistration = async (registrationNumber: string) => {
    setSearchingRegistrations(true);
    setSearchError('');
    try {
      const results = await msbsApi.searchForRegistrations(dispensaryId, { registrationNumber });
      if (results.length) {
        const newRows = results.filter((r) => r.s3_key).map((r) => mapRegistrationToRow(r, true));
        setRegistrationRows((prevRows) => {
          const existingRows = prevRows.filter((r) => !newRows.some((nr) => nr.data.id === r.data.id));
          return newRows.concat(existingRows);
        });
      } else {
        setSearchError('No registration found with that number. Check the number and try again.');
      }
    } catch {
      getSnackbarStore().showErrorSnackbarMessage(
        'There was an issue searching for registrations. Please contact support'
      );
    } finally {
      setSearchingRegistrations(false);
    }
  };

  return (
    <Dialog
      action={
        <Button
          color="primary"
          label="Submit"
          onClick={() => onSubmit(selectedRegistrations)}
          style={{ minWidth: isMobile ? '100%' : '100px' }}
          dataCy="submit-msbs-button"
          disabled={selectedRegistrations.length === 0}
        />
      }
      onCancel={onCancel}
      handleClose={onClose}
      isOpen={isOpen}
      title="MSB Registrations"
      width={isTablet ? '85vw' : '898px'}
    >
      {isLoadingMsbRegistrationSearchResults ? (
        <Spinner text="Searching registrations..." />
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Text content="We searched the FinCEN MSB Registry for registrations matching your business name and address. Select your business(es) below and click Submit to attach the registration to this requirement." />
          <TableContainer>
            <Table
              minWidth={590}
              noPagination
              selectable
              columns={msbColumns}
              defaultSort="name"
              defaultSortDirection="asc"
              defaultSelected={registrationRows
                .filter((r) => alreadyUploadedRegNumbers.includes(r.data.number))
                .map((r) => r.id)}
              noDataComponent={() => (
                <ZeroState
                  noBorder
                  titleFont="medium"
                  title="No matching registrations found"
                  alignSubTitle="center"
                  subTitle={
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        textAlign: 'center',
                        maxWidth: '70%',
                        justifySelf: 'center'
                      }}
                    >
                      <Text
                        type="body2"
                        sx={{ color: 'text.secondary' }}
                        content="Your registration may be too new to appear in FinCEN's registry, or your business info in Green Check may not match your filing."
                      />
                    </Box>
                  }
                  icon={<img src={'/img/empty-states/no-results.svg'} width={'50px'} height={'50px'} />}
                />
              )}
              rows={registrationRows}
              returnSelection={(selectedIds) => {
                const registrations = registrationRows?.filter(
                  (r) => selectedIds.includes(r.id) && !alreadyUploadedRegNumbers.includes(r.data.number)
                );
                if (registrations) {
                  setSelectedRegistrations(registrations.map((r) => r.data.rawRecord));
                }
              }}
            />
          </TableContainer>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              paddingBottom: 4
            }}
          >
            <InputLabel
              label={`Don't see your business? Search by MSB registration number or upload manually.`}
              name={'registrationNumber'}
            />
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                alignItems: 'flex-start'
              }}
            >
              <Box sx={{ width: '70%' }}>
                <InputText
                  label={``}
                  noLabel
                  name={'registrationNumber'}
                  rules={{
                    minLength: {
                      value: 14,
                      message: 'Registration number must be 14 digits starting with 31000...'
                    },
                    maxLength: {
                      value: 14,
                      message: 'Registration number must be 14 digits starting with 31000...'
                    },
                    required: {
                      value: true,
                      message: 'Please enter a registration number to search for'
                    },
                    pattern: {
                      value: /^31000\d{9}$/,
                      message: 'Registration number must be 14 digits starting with 31000...'
                    }
                  }}
                  {...form}
                />
              </Box>
              <Button
                label={'Search'}
                onClick={form.handleSubmit((data) => {
                  searchForRegistration(data.registrationNumber);
                })}
                color={'primary'}
                isLoading={searchingRegistrations}
              />
              <Button label={'Upload Manually'} onClick={showUploadForm} color={'primary-outlined'} />
            </Box>
            {searchError && <Error content={searchError} />}
          </Box>
        </Box>
      )}
    </Dialog>
  );
};
